/**
 * COMPOST EFFECTS — the six effects a compost can carry. The scrap composter (sim/composter.ts) decides WHICH
 * effects a compost gets and how strong (by its rank); this file says what each one IS and how it reads.
 *
 * Four are CHANCES paid at harvest (useGreenhouse `harvestPot`); two are PASSIVES read continuously — Growth
 * Speed (plantGrowth `compostSpeedBonus`) and Moisture Lock (moisture.ts `retention`). Every amount is a whole
 * percent. The ids are what saves store, so they never change; the names are free to.
 */

export type CompostEffectId = "double" | "xp" | "value" | "quickBloom" | "pollinate" | "retain";

export interface CompostEffectDef {
  id: CompostEffectId;
  name: string;
  icon: string;
  blurb: string;
  /** true = the amount is a CHANCE to proc at harvest; false = a continuous PASSIVE. */
  chanceBased: boolean;
}

const COMPOST_EFFECTS: Record<CompostEffectId, CompostEffectDef> = {
  double:     { id: "double",     name: "Double Harvest", icon: "🌾", chanceBased: true,  blurb: "A chance to harvest two flowers instead of one." },
  xp:         { id: "xp",         name: "Bonus XP",       icon: "✨", chanceBased: true,  blurb: "A chance for a burst of extra XP when harvested." },
  value:      { id: "value",      name: "Rich Harvest",   icon: "💰", chanceBased: true,  blurb: "A chance for bonus coins the moment it's harvested." },
  quickBloom: { id: "quickBloom", name: "Growth Speed",   icon: "🌱", chanceBased: false, blurb: "Every plant grown in this pot grows faster." },
  pollinate:  { id: "pollinate",  name: "Seed Return",    icon: "🌰", chanceBased: true,  blurb: "A chance to get the sown seed back when harvested." },
  retain:     { id: "retain",     name: "Moisture Lock",  icon: "💧", chanceBased: false, blurb: "The soil dries more slowly and eases toward wet." },
};
export const COMPOST_EFFECT_IDS: CompostEffectId[] = ["double", "xp", "value", "quickBloom", "pollinate", "retain"];

export function effectDef(id: CompostEffectId): CompostEffectDef { return COMPOST_EFFECTS[id]; }

/** What a RICH HARVEST proc pays: this share of the plant's sell value, in coins, at harvest. */
export const VALUE_PROC_SHARE = 0.5;
/** What a BONUS XP proc pays: this share of the plant's sell value, as XP, at harvest. */
export const XP_PROC_SHARE = 0.4;

/** One effect in plain words with its amount, e.g. "28% chance of +50% coins", "+12% growth speed",
 *  "40% slower drying". Chance effects always say what they pay; passives never say "chance". */
export function effectPhrase(id: CompostEffectId, amount: number): string {
  switch (id) {
    case "double": return `${amount}% chance of a double harvest`;
    case "xp": return `${amount}% chance of bonus XP (+${Math.round(XP_PROC_SHARE * 100)}% of its value)`;
    case "value": return `${amount}% chance of +${Math.round(VALUE_PROC_SHARE * 100)}% coins`;
    case "pollinate": return `${amount}% chance to get the seed back`;
    case "quickBloom": return `+${amount}% growth speed`;
    case "retain": return `${amount}% slower drying`;
  }
}
